import React, { useEffect, useState, useCallback } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HeatmapModule from 'highcharts/modules/heatmap';
import HighchartsExporting from 'highcharts/modules/exporting';
import { Filters } from './Filters';
import { useSelector } from 'react-redux';
import { fetchData, formatNumber } from '../../utils';
import Loading from '../../components/Loading';
import DisplayModeToggle from '../../components/DisplayModeToggle';
import ScreenStatePanel from '../../components/Insights/ScreenStatePanel';
import useRetentionScreenState from '../../hooks/useRetentionScreenState';
import { sendRequest } from '../../utils/dateHelpers';
import {
  getRetentionScreenOverlayContent,
  RETENTION_SCREEN_STATES
} from '../../constants/retentionScreenState';
import {
  getAverageDeveloperCostFromSource,
  parseNumericInput,
  shouldFetchAverageDeveloperCostFromEndpoint
} from '../../utils/companyConfigurations';
import {
  getCohortCellTheme,
  getRetentionHeatmapStops
} from '../../utils/heatmapTheme';

HeatmapModule(Highcharts);
HighchartsExporting(Highcharts);

const DISPLAY_MODES = {
  REAL_EFFORT: 'git_effort',
  INVESTMENT: 'investment'
};

const getAverageDeveloperCost = userInfo => {
  const directCost = getAverageDeveloperCostFromSource(userInfo);

  if (directCost > 0) return directCost;

  return getAverageDeveloperCostFromSource(userInfo?.user);
};

const Retention = () => {
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const { userInfo = {} } = useSelector(state => state);
  const [chartOptions, setChartOptions] = useState({});
  const [hasData, setHasData] = useState(false);
  const [displayMode, setDisplayMode] = useState(DISPLAY_MODES.REAL_EFFORT);
  const [isDarkMode, setIsDarkMode] = useState(
    () => document?.body?.classList.contains('dark') || false
  );
  const [fallbackAverageDeveloperCost, setFallbackAverageDeveloperCost] =
    useState(0);
  const {
    loading: retentionScreenStateLoading,
    screenState: baseRetentionScreenState,
    screenStateMeta
  } = useRetentionScreenState();

  const companyUid = userInfo.user?.companyId;
  const roleBasedAverageDeveloperCost = getAverageDeveloperCost(userInfo);
  const averageDeveloperCost =
    roleBasedAverageDeveloperCost > 0
      ? roleBasedAverageDeveloperCost
      : fallbackAverageDeveloperCost;
  const hasClientSideInvestmentRate = averageDeveloperCost > 0;
  const shouldRequestServerCalculatedInvestment =
    displayMode === DISPLAY_MODES.INVESTMENT && !hasClientSideInvestmentRate;
  const resolvedRetentionScreenState =
    baseRetentionScreenState !== RETENTION_SCREEN_STATES.READY
      ? baseRetentionScreenState
      : hasData
        ? RETENTION_SCREEN_STATES.READY
        : RETENTION_SCREEN_STATES.ANALYSIS_PENDING;
  const retentionOverlayContent = getRetentionScreenOverlayContent(
    resolvedRetentionScreenState,
    screenStateMeta
  );

  useEffect(() => {
    const syncDarkMode = () => {
      setIsDarkMode(document.body.classList.contains('dark'));
    };

    syncDarkMode();

    const observer = new MutationObserver(syncDarkMode);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchAverageDeveloperCost = async () => {
      try {
        const response = await sendRequest('/company-configurations', 'GET');
        const fallbackCost = getAverageDeveloperCostFromSource(response);

        if (fallbackCost > 0) {
          setFallbackAverageDeveloperCost(fallbackCost);
        }
      } catch (error) {
        console.error('Failed to load fallback average developer cost', error);
      }
    };

    if (
      roleBasedAverageDeveloperCost > 0 ||
      !shouldFetchAverageDeveloperCostFromEndpoint(
        userInfo?.role,
        userInfo?.user?.role
      )
    ) {
      return;
    }

    fetchAverageDeveloperCost();
  }, [roleBasedAverageDeveloperCost, userInfo?.role, userInfo?.user?.role]);

  const prepareData = useCallback(
    (metric, data) => {
      const rawData = data;
      const baseXCategories = [...new Set(rawData.map(point => point.x))].sort(
        (a, b) => a - b
      );
      const yCategories = [...new Set(rawData.map(point => point.y))].sort();
      const xOrderMap = baseXCategories.reduce((acc, category, index) => {
        acc[String(category)] = index;
        return acc;
      }, {});

      const cohortStartByCategory = rawData.reduce((acc, point) => {
        const yCategory = point?.y;
        const pointXOrder = xOrderMap[String(point?.x)];
        const existingPoint = acc[yCategory];
        const existingXOrder = xOrderMap[String(existingPoint?.x)];

        if (
          !existingPoint ||
          (Number.isInteger(pointXOrder) &&
            (!Number.isInteger(existingXOrder) || pointXOrder < existingXOrder))
        ) {
          acc[yCategory] = point;
        }

        return acc;
      }, {});

      const isInvestment = displayMode === DISPLAY_MODES.INVESTMENT;
      const firstColumnLabel = isInvestment
        ? 'Estimated Cost Without AI ($)'
        : 'REV';
      const xCategories = [firstColumnLabel, ...baseXCategories];

      const heatmapData = rawData.map(point => ({
        x: baseXCategories.indexOf(point.x) + 1,
        y: yCategories.indexOf(point.y),
        value: Math.round(point?.[metric] || 0),
        isCohortValue: false
      }));

      const cohortValuesData = yCategories.map((category, index) => {
        const cohortCellTheme = getCohortCellTheme(isDarkMode);
        const cohortStartPoint = cohortStartByCategory?.[category];
        const revValue = parseNumericInput(cohortStartPoint?.rev);
        const normalizedRev = Number.isFinite(revValue) ? revValue : 0;
        const value = isInvestment
          ? Math.round((normalizedRev / 60) * averageDeveloperCost)
          : Math.round(normalizedRev);

        return {
          x: 0,
          y: index,
          value,
          isCohortValue: true,
          color: cohortCellTheme.fill,
          borderColor: cohortCellTheme.border
        };
      });

      const mergedHeatmapData = [...cohortValuesData, ...heatmapData];

      return {
        xCategories,
        yCategories,
        heatmapData: mergedHeatmapData,
        isInvestment
      };
    },
    [averageDeveloperCost, displayMode, isDarkMode]
  );

  const createChartConfig = useCallback(
    (metric, data) => {
      const { xCategories, yCategories, heatmapData, isInvestment } =
        prepareData(metric, data);

      return {
        chart: {
          type: 'heatmap',
          height: 560
        },
        credits: {
          enabled: false
        },
        title: {
          text: ''
        },
        xAxis: {
          categories: xCategories,
          opposite: true,
          labels: {
            style: {
              color: isDarkMode ? '#cbd5e1' : '#475569'
            }
          },
          lineColor: isDarkMode ? '#334155' : '#cbd5e1',
          tickColor: isDarkMode ? '#334155' : '#cbd5e1'
        },
        yAxis: {
          categories: yCategories,
          title: null,
          reversed: true,
          labels: {
            style: {
              color: isDarkMode ? '#cbd5e1' : '#475569'
            }
          },
          gridLineColor: isDarkMode ? '#334155' : '#e2e8f0'
        },
        colorAxis: {
          min: 0,
          max: 100,
          stops: getRetentionHeatmapStops(isDarkMode)
        },
        tooltip: {
          useHTML: true,
          outside: true,
          formatter: function () {
            const pointValue = Math.round(this.point?.value || 0);
            const isCohortValue = Boolean(this.point?.isCohortValue);
            const cohortLabel =
              this.series.yAxis.categories?.[this.point.y] || 'Cohort';
            const periodLabel =
              this.series.xAxis.categories?.[this.point.x] || 'Period';
            const metricLabel = isCohortValue
              ? isInvestment
                ? 'Estimated Cost Without AI'
                : 'REV'
              : 'Retention';
            const valueLabel = isCohortValue
              ? isInvestment
                ? `$${formatNumber(pointValue)}`
                : formatNumber(pointValue)
              : `${pointValue}%`;
            const accentColor = this.point?.color || '#38bdf8';
            const surface = isDarkMode
              ? 'linear-gradient(180deg, rgba(15,23,42,0.98), rgba(15,23,42,0.94))'
              : 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.96))';
            const borderColor = isDarkMode
              ? 'rgba(148,163,184,0.22)'
              : 'rgba(148,163,184,0.28)';
            const titleColor = isDarkMode ? '#f8fafc' : '#0f172a';
            const mutedColor = isDarkMode ? '#94a3b8' : '#64748b';
            const strongColor = isDarkMode ? '#e2e8f0' : '#0f172a';
            const chipBg = isDarkMode
              ? 'rgba(148,163,184,0.12)'
              : 'rgba(15,23,42,0.05)';
            const glowColor = isDarkMode
              ? 'rgba(56,189,248,0.12)'
              : 'rgba(250,204,21,0.14)';

            return `
              <div style="min-width:168px;max-width:196px;padding:0;border-radius:12px;background:${surface};border:1px solid ${borderColor};box-shadow:0 8px 20px rgba(15,23,42,0.14);backdrop-filter:blur(10px);overflow:hidden;">
                <div style="padding:10px 10px 9px 10px;background:radial-gradient(circle at top left, ${glowColor}, transparent 32%);">
                  <div style="display:flex;align-items:center;justify-content:space-between;gap:7px;margin-bottom:7px;">
                    <div style="display:flex;align-items:center;gap:8px;min-width:0;">
                      <span style="width:8px;height:8px;border-radius:999px;background:${accentColor};box-shadow:0 0 0 3px ${
                        isDarkMode
                          ? 'rgba(56,189,248,0.14)'
                          : 'rgba(56,189,248,0.16)'
                      };flex-shrink:0;"></span>
                      <span style="font-size:11px;font-weight:700;color:${titleColor};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
                        ${cohortLabel}
                      </span>
                    </div>
                    <span style="display:inline-flex;align-items:center;border-radius:999px;padding:2px 6px;background:${chipBg};font-size:9px;font-weight:700;letter-spacing:0.02em;color:${mutedColor};flex-shrink:0;">
                      ${isCohortValue ? 'Cohort' : periodLabel}
                    </span>
                  </div>
                  ${
                    !isCohortValue
                      ? `<div style="font-size:10px;color:${mutedColor};margin-bottom:8px;">Period: <span style="color:${strongColor};font-weight:600;">${periodLabel}</span></div>`
                      : ''
                  }
                  <div style="display:flex;align-items:flex-end;justify-content:space-between;gap:8px;">
                    <div>
                      <div style="font-size:9px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:${mutedColor};margin-bottom:4px;">
                        ${metricLabel}
                      </div>
                      <div style="font-size:19px;line-height:1;font-weight:800;color:${titleColor};">
                        ${valueLabel}
                      </div>
                    </div>
                    <div style="font-size:10px;color:${mutedColor};text-align:right;">
                      ${isCohortValue ? 'Starting value' : 'Cell value'}
                    </div>
                  </div>
                </div>
              </div>
            `;
          }
        },
        series: [
          {
            type: 'heatmap',
            data: heatmapData,
            borderWidth: 1,
            dataLabels: {
              enabled: true,
              style: {
                color: isDarkMode ? '#e2e8f0' : '#0f172a',
                textOutline: 'none'
              },
              formatter: function () {
                if (this.point.isCohortValue) {
                  const roundedValue = Math.round(this.point.value);
                  const formattedValue = formatNumber(roundedValue);
                  return isInvestment
                    ? `$${formattedValue}`
                    : `${formattedValue}`;
                }

                return `${Math.round(this.point.value)}%`;
              }
            },
            states: {
              hover: {
                brightness: isDarkMode ? 0.08 : 0.04
              }
            }
          }
        ]
      };
    },
    [isDarkMode, prepareData]
  );

  const getData = useCallback(async () => {
    try {
      setLoading(true);
      const dataGroups = {};
      const requestFilters = {
        ...filters,
        operation: shouldRequestServerCalculatedInvestment
          ? DISPLAY_MODES.INVESTMENT
          : DISPLAY_MODES.REAL_EFFORT
      };
      const responseData = await fetchData(
        requestFilters,
        dataGroups,
        '/retention/data'
      );

      const metric =
        filters.calculationsMethod === 'ratio'
          ? 'start_value_ratio'
          : 'start_value';

      if (responseData?.[0]?.retention) {
        const updatedOptions = createChartConfig(
          metric,
          responseData[0].retention
        );
        setChartOptions(updatedOptions);
        setHasData(responseData[0].retention.length > 0);
      } else {
        const updatedOptions = createChartConfig(metric, []);
        setChartOptions(updatedOptions);
        setHasData(false);
      }
    } catch (error) {
      const metric =
        filters.calculationsMethod === 'ratio'
          ? 'start_value_ratio'
          : 'start_value';
      const updatedOptions = createChartConfig(metric, []);
      setChartOptions(updatedOptions);
      setHasData(false);
    } finally {
      setLoading(false);
    }
  }, [filters, createChartConfig, shouldRequestServerCalculatedInvestment]);

  useEffect(() => {
    if (retentionScreenStateLoading) {
      return;
    }

    if (baseRetentionScreenState !== RETENTION_SCREEN_STATES.READY) {
      setLoading(false);
      return;
    }

    if (Object.keys(filters).length && companyUid) {
      getData();
    }
  }, [
    baseRetentionScreenState,
    companyUid,
    filters,
    getData,
    retentionScreenStateLoading
  ]);

  const handleFilters = useCallback(nextFilters => {
    const normalizedFilters = {
      ...nextFilters,
      buttonGroup: nextFilters.buttonGroup || 'Overview'
    };

    setFilters(normalizedFilters);
  }, []);

  return (
    <div className="relative">
      <div>
        <div className="pb-3 flex flex-wrap gap-3">
          <Filters
            userInfo={userInfo}
            isNotMulti={['authors', 'projects', 'repos', 'tags']}
            isHidden={['categories', 'operation']}
            onChange={handleFilters}
            retentionCategoriesVisible={true}
            showContextSummary={true}
            summaryCategory="retention"
            summaryMetric={displayMode}
            afterViewType={
              <DisplayModeToggle
                value={displayMode}
                onChange={setDisplayMode}
              />
            }
          />
        </div>
        <div className="rounded-sm border border-stroke bg-white p-4 shadow-default dark:border-strokedark dark:bg-boxdark">
          {loading || retentionScreenStateLoading ? (
            <Loading type="retention" />
          ) : retentionOverlayContent ? (
            <ScreenStatePanel {...retentionOverlayContent} />
          ) : (
            <HighchartsReact
              highcharts={Highcharts}
              options={chartOptions}
              containerProps={{ className: 'retention-chart-host' }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Retention;
